import { inject, Injectable } from '@angular/core';
import { ApBaseClass, SingleSub } from 'webcommon/shared';

import { AprimaAnalyticsService } from './AprimaAnalyticsService';
import { BridgeService } from './BridgeService';
import { CrossWindow } from 'webcommon/shared';
import { TransitionManagerService } from './navigation/TransitionManagerService';
import { WebCommonStorageRepository } from './WebCommonStorageRepository';

@Injectable({
  providedIn: 'root',
})
export class LogOffService extends ApBaseClass {
  static readonly ajsFactoryName = 'LogOffServiceTs';

  private readonly crossWindow = inject(CrossWindow);

  private globalEventsSingleSub = SingleSub.create(this);

  constructor(
    private aprimaAnalyticsService: AprimaAnalyticsService,
    private bridge: BridgeService,
    private transitionManagerService: TransitionManagerService,
    private webCommonStorageRepository: WebCommonStorageRepository,
  ) {
    super();
  }

  // Call globally to allow this to handle CrossWindow events.
  // technically, this could be opt-in per app.
  listenGlobalEvents(): void {
    this.globalEventsSingleSub.start(this.crossWindow.on('aprima-logoff-sync').subscribe((_value) => {
      this.transitionManagerService.transitionToLogin();
    }));
  }

  stopGlobalEvents(): void {
    this.globalEventsSingleSub.stop();
  }

  private notifyCrossTabLogoff(): void {
    this.crossWindow.fireEvent('aprima-logoff-sync', null);
  }

  // This is kind of a misnomer.
  // Currently, most places that call this are expected to navigate somewhere after it's done,
  // instead of that part being handled in this method
  // be careful modifying the behavior of this as it may be called in ajs
  async logoff(): Promise<void> {
    this.webCommonStorageRepository.removeJsonWebToken();
    this.webCommonStorageRepository.removeUserId();
    this.aprimaAnalyticsService.sendEvent('user', 'click', 'logoff', '0');

    // this event is only used by Aprima NOW mobile app right now (which it doesn't seem like we are updating)
    this.bridge.fireEvent('logoff', {
      message: 'logoff',
    });
    return Promise.resolve();
  }

  // logoff all tabs, and redirect to default login page
  // TODO: There are some places not calling this right now, because they are doing a different navigation after the logoff() call,
  //  like CurrentUserService.forceLogoffUser(), and a number of places in ajs in AprimaWeb, PrmWeb, AppMobile, Facility-Portal, etc.
  //  I'm not 100% sure if all of these usages are supposed to work the same, and we don't want to double-transition.
  //  For now, I'm not changing the behavior of all those other places,
  //  and I guess instead we can opt-in as needed by calling this instead of logoff().
  //  Also, for the case of logoffFromUnauthorizedResponse, shouldn't you be able to login on this tab, and then still go switch tabs,
  //  without losing ur state on that other tab, or no?
  async logoffAll(): Promise<void> {
    await this.logoff();
    this.notifyCrossTabLogoff();
    this.transitionManagerService.transitionToLogin();
  }

  logoffFromUnauthorizedResponse(): void {
    const deviceId = this.webCommonStorageRepository.getDeviceId();
    const jwt = this.webCommonStorageRepository.getJsonWebToken();
    const jwtDesc = typeof jwt === 'undefined' ? 'undefined' : jwt ? jwt.length : '"falsy token"';
    const msg = 'User logged out by 401. Redirected to login. DeviceId=' + deviceId + ' JWT:' + jwtDesc;

    // eslint-disable-next-line no-console
    console.info(msg);
    this.aprimaAnalyticsService.sendException(msg);

    this.transitionManagerService.transitionToLogin();
  }

  logoffFromFailedLoginRedirect(loginUrlFromRedirect: string | undefined): void {
    this.transitionManagerService.transitionToLogin(loginUrlFromRedirect);
  }
}
